import React from 'react';

import { motion, AnimatePresence } from 'framer-motion';

import { Button, type ButtonProps } from 'components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from 'components/ui/dialog';
import { modalTokens, modalSizes, modalAnimations, modalVariants } from 'lib/design-tokens';
import { cn } from 'lib/utils';

/**
 * Predefined modal sizes for consistent UI
 * - sm: 400px - Confirmation dialogs, simple messages
 * - md: 600px - Standard forms (default)
 * - lg: 700px - Forms with tabs, wizards
 * - xl: 900px - Complex modals, large content
 * - full: 95vw - Full width modals
 */
type ModalSize = 'sm' | 'md' | 'lg' | 'xl' | 'full';

/**
 * Semantic variants for contextual modals
 */
type ModalVariant = 'default' | 'success' | 'warning' | 'error' | 'info';

/**
 * Header style variants
 */
type HeaderVariant = 'default' | 'gradient' | 'accent';

interface GlobalModalProps {
  open: boolean;
  title: string;
  description?: string;
  onClose: () => void;
  onConfirm?: () => void;
  onCancel?: () => void;
  confirmButtonText?: string;
  cancelButtonText?: string;
  confirmDisabled?: boolean;
  cancelDisabled?: boolean;
  children?: React.ReactNode;
  /**
   * Custom width for the modal.
   * @deprecated Use `size` prop for standardized sizes. This prop is kept for backward compatibility.
   */
  width?: string;
  /**
   * Minimum height for the modal.
   * @deprecated Use `maxHeight` for controlling overflow. This prop is kept for backward compatibility.
   */
  height?: string;
  /** @deprecated Not used in shadcn Dialog - kept for backward compatibility */
  keepMounted?: boolean;
  closeOnBackdropClick?: boolean;
  closeOnEscapeKeyDown?: boolean;
  widthButtons?: string;
  /**
   * Predefined modal size. Overridden by `width` prop if both are provided.
   * @default 'md'
   */
  size?: ModalSize;
  /**
   * Maximum height of the modal content area.
   * @default '90vh'
   */
  maxHeight?: string;
  /**
   * Hide the default footer with confirm/cancel buttons.
   * Useful when the modal content has its own buttons (e.g., wizards, tabs).
   * @default false
   */
  hideDefaultFooter?: boolean;
  /**
   * Hide the default header with title and description.
   * Useful when the modal content has a custom header.
   * @default false
   */
  hideDefaultHeader?: boolean;
  /**
   * Button variant for the confirm button.
   * Use 'destructive' for dangerous actions like delete or cancel operations.
   * @default 'primary'
   */
  confirmVariant?: ButtonProps['variant'];
  /**
   * Button variant for the cancel button.
   * @default 'outline'
   */
  cancelVariant?: ButtonProps['variant'];
  /**
   * Show loading spinner on confirm button and disable it.
   * @default false
   */
  confirmLoading?: boolean;
  /**
   * Additional CSS classes for the modal content container.
   */
  className?: string;
  /**
   * Additional CSS classes for the modal body (children container).
   */
  bodyClassName?: string;
  /**
   * Remove default padding from body. Useful for modals with custom layouts.
   * @default false
   */
  noPadding?: boolean;
  /**
   * Optional icon to display in the header.
   * Renders centered above the title.
   */
  icon?: React.ReactNode;
  /**
   * Header style variant.
   * - 'default': Standard header with border
   * - 'gradient': Subtle gradient background
   * - 'accent': Accent color tint
   * @default 'default'
   */
  headerVariant?: HeaderVariant;
  /**
   * Semantic variant that applies contextual colors.
   * Affects icon container and accent colors.
   * @default 'default'
   */
  variant?: ModalVariant;
  /**
   * Enable entrance/exit animations.
   * @default true
   */
  animate?: boolean;
  /**
   * Show the close (X) button in the top-right corner.
   * @default true
   */
  showCloseButton?: boolean;
}

const GlobalModal: React.FC<GlobalModalProps> = ({
  open,
  title,
  description,
  onClose,
  onConfirm,
  onCancel,
  confirmButtonText = 'Confirmar',
  cancelButtonText = 'Cancelar',
  confirmDisabled = false,
  cancelDisabled = false,
  children,
  width,
  height = '20rem',
  closeOnBackdropClick = false,
  closeOnEscapeKeyDown = false,
  widthButtons = '100%',
  size = 'md',
  maxHeight = '90vh',
  hideDefaultFooter = false,
  hideDefaultHeader = false,
  confirmVariant = 'default',
  cancelVariant = 'outline',
  confirmLoading = false,
  className,
  bodyClassName,
  noPadding = false,
  icon,
  headerVariant = 'default',
  variant = 'default',
  animate = true,
  showCloseButton = true,
}) => {
  // Get variant-specific styles
  const variantStyles = modalVariants[variant];

  // Determine header classes based on variant
  const headerClasses = {
    default: modalTokens.header.base,
    gradient: modalTokens.header.gradient,
    accent: modalTokens.header.accent,
  }[headerVariant];

  // Get icon container classes based on variant
  const iconContainerClasses = {
    default: modalTokens.iconContainer.base,
    success: modalTokens.iconContainer.success,
    warning: modalTokens.iconContainer.warning,
    error: modalTokens.iconContainer.error,
    info: modalTokens.iconContainer.info,
  }[variant];

  const handleOpenChange = (isOpen: boolean) => {
    // Always call onClose when Dialog wants to close (X button, etc.)
    // Backdrop and Escape are handled separately by onInteractOutside and onEscapeKeyDown
    if (!isOpen) {
      onClose();
    }
  };

  const handleEscapeKeyDown = (event: KeyboardEvent) => {
    if (!closeOnEscapeKeyDown) {
      event.preventDefault();
    }
  };

  // Determine if we should show the default footer
  const showDefaultFooter = !hideDefaultFooter && (onCancel || onConfirm);

  // Animation wrapper component
  const MotionWrapper = animate ? motion.div : 'div';
  const animationProps = animate ? modalAnimations.content : {};

  // Responsive size classes
  const sizeClasses = modalSizes[size];

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent
        className={cn(
          // Base glassmorphism
          modalTokens.container.glass,
          // Ring/glow effect
          modalTokens.ring.focus,
          modalTokens.ring.glow,
          // Layout
          'rounded-2xl',
          'p-0',
          'gap-0',
          'overflow-hidden',
          'flex',
          'flex-col',
          // Responsive sizing
          sizeClasses,
          // Variant accent border
          variantStyles.accent,
          // Hide default close button if needed
          !showCloseButton && '[&>button]:hidden',
          className
        )}
        style={{
          minWidth: width,
          width: width,
          maxWidth: width,
          minHeight: height,
          maxHeight: maxHeight,
        }}
        onInteractOutside={e => {
          if (!closeOnBackdropClick) {
            e.preventDefault();
          }
        }}
        onEscapeKeyDown={handleEscapeKeyDown}
      >
        <AnimatePresence mode="wait">
          {open && (
            <MotionWrapper {...animationProps} className="flex flex-col flex-1 min-h-0">
              {!hideDefaultHeader && (
                <DialogHeader className={cn(headerClasses, 'shrink-0')}>
                  {/* Icon */}
                  {icon && (
                    <div className={cn(iconContainerClasses, variantStyles.icon)}>{icon}</div>
                  )}

                  {/* Title */}
                  <DialogTitle className="text-center text-xl font-semibold tracking-wide">
                    {title}
                  </DialogTitle>

                  {/* Description */}
                  {description && (
                    <DialogDescription className="text-center mt-2 text-muted-foreground whitespace-pre-line">
                      {description}
                    </DialogDescription>
                  )}
                </DialogHeader>
              )}

              {/* Body */}
              {children && (
                <div
                  className={cn(
                    noPadding ? modalTokens.body.noPadding : modalTokens.body.scrollable,
                    bodyClassName
                  )}
                >
                  {children}
                </div>
              )}

              {/* Footer */}
              {showDefaultFooter && (
                <DialogFooter
                  className={cn(
                    modalTokens.footer.withSeparator,
                    widthButtons !== '100%' ? 'justify-center' : 'justify-between',
                    'flex-row',
                    'gap-4'
                  )}
                  style={{
                    width: widthButtons,
                    margin: widthButtons !== '100%' ? '0 auto' : undefined,
                  }}
                >
                  {onCancel && (
                    <Button
                      variant={cancelVariant}
                      onClick={onCancel}
                      fullWidth
                      disabled={cancelDisabled || confirmLoading}
                    >
                      {cancelButtonText}
                    </Button>
                  )}
                  {onConfirm && (
                    <Button
                      variant={confirmVariant}
                      onClick={onConfirm}
                      fullWidth
                      disabled={confirmDisabled || confirmLoading}
                      loading={confirmLoading}
                      className={onCancel ? 'ml-4' : undefined}
                    >
                      {confirmButtonText}
                    </Button>
                  )}
                </DialogFooter>
              )}

              {/* Loading overlay */}
              {confirmLoading && (
                <div className="absolute inset-0 bg-background/50 backdrop-blur-[2px] flex items-center justify-center z-10">
                  <div className="w-8 h-8 border-2 border-primary/30 border-t-primary rounded-full animate-spin" />
                </div>
              )}
            </MotionWrapper>
          )}
        </AnimatePresence>
      </DialogContent>
    </Dialog>
  );
};

export default GlobalModal;
export { type GlobalModalProps, type ModalSize, type ModalVariant, type HeaderVariant };
